import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Container,
  Grid,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import { useToast } from "../components/ui/use-toast";
import { useNavigation } from "../lib/NavigationContext";
import QuoteForm from "../components/mobility/QuoteForm";
import QuoteResults from "../components/mobility/QuoteResults";
import {
  resolveZone,
  getZoneDisplayName,
} from "../components/mobility/data/zoneMap";
import {
  getRate,
  calculatePremium,
  RATE_DIVISOR,
} from "../components/mobility/data/rateTable";
import { PACKAGE_TYPES } from "../components/mobility/data/packageCatalog";

const STORAGE_KEY = "mobility_safe_last_quote";

export default function QuotePage() {
  const { navigateTo } = useNavigation();
  const { toast } = useToast();
  const initialFormData = {
    plate: "",
    brand: "",
    model: "",
    provinceCode: "",
    province: "",
    vehicleValue: "",
    duration: "24",
    firstName: "",
    lastName: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [quote, setQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.formData) setFormData(parsed.formData);
        if (parsed.quote) setQuote(parsed.quote);
      } catch (e) {}
    }
  }, []);

  const saveToStorage = (data) =>
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

  const validateForm = () => {
    const newErrors = {};
    if (!formData.provinceCode)
      newErrors.province = "Seleziona la provincia di residenza";
    if (!formData.vehicleValue || parseFloat(formData.vehicleValue) <= 0)
      newErrors.vehicleValue = "Inserisci un valore veicolo valido";
    if (!formData.duration)
      newErrors.duration = "Seleziona la durata della copertura";
    if (!formData.plate) {
      if (!formData.brand) newErrors.brand = "Inserisci la marca del veicolo";
      if (!formData.model) newErrors.model = "Inserisci il modello del veicolo";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateQuote = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const zone = resolveZone(formData.provinceCode);
      const vehicleValue = parseFloat(formData.vehicleValue);
      const duration = parseInt(formData.duration);
      const prices = {};
      Object.values(PACKAGE_TYPES).forEach((pkgType) => {
        const rate = getRate(zone, pkgType, duration);
        prices[pkgType] = rate
          ? calculatePremium(vehicleValue, rate, RATE_DIVISOR)
          : 0;
      });
      let vehicleInfo = formData.plate
        ? `${formData.plate}${
            formData.brand || formData.model
              ? ` (${[formData.brand, formData.model]
                  .filter(Boolean)
                  .join(" ")})`
              : ""
          }`
        : [formData.brand, formData.model].filter(Boolean).join(" ");
      const customerName =
        [formData.firstName, formData.lastName].filter(Boolean).join(" ") ||
        null;
      const newQuote = {
        id: Date.now().toString(),
        zone,
        zoneName: getZoneDisplayName(zone),
        province: formData.province,
        provinceCode: formData.provinceCode,
        vehicleValue,
        duration: formData.duration,
        vehicleInfo,
        customerName,
        prices,
        status: "DRAFT",
        createdAt: new Date().toISOString(),
      };
      setQuote(newQuote);
      saveToStorage({ formData, quote: newQuote });
      toast({
        title: "Preventivo calcolato",
        description: `Zona ${zone} - ${
          Object.keys(prices).length
        } pacchetti disponibili`,
      });
    } catch (error) {
      toast({
        title: "Errore",
        description: "Si è verificato un errore nel calcolo del preventivo",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuoteAction = (action, packageType) => {
    const labels = {
      draft: "salvato come bozza",
      send: "inviato al cliente",
      issue: "emesso",
    };
    const descs = {
      draft: "Il preventivo è stato salvato e può essere modificato",
      send: "Il preventivo è stato inviato via email al cliente",
      issue: "La polizza è stata emessa e registrata nel sistema",
    };
    toast({
      title: `Preventivo ${labels[action]}`,
      description: `${descs[action]} - Pacchetto ${packageType}`,
    });
    if (quote) {
      const newStatus =
        action === "issue" ? "ISSUED" : action === "send" ? "SENT" : "DRAFT";
      const updatedQuote = {
        ...quote,
        status: newStatus,
        selectedPackage: packageType,
        lastAction: action,
        lastActionDate: new Date().toISOString(),
      };
      setQuote(updatedQuote);
      saveToStorage({ formData, quote: updatedQuote });
    }
  };

  const handleNewQuote = () => {
    setFormData(initialFormData);
    setQuote(null);
    setErrors({});
    localStorage.removeItem(STORAGE_KEY);
    toast({
      title: "Nuovo preventivo",
      description: "Pronto per calcolare un nuovo preventivo",
    });
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {quote && (
          <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleNewQuote}
            >
              Nuovo Preventivo
            </Button>
          </Box>
        )}

        <Grid container spacing={4}>
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: "sticky", top: 80 }}>
              <QuoteForm
                formData={formData}
                setFormData={setFormData}
                onCalculate={calculateQuote}
                isLoading={isLoading}
                errors={errors}
              />
            </Box>
          </Grid>
          <Grid item xs={12} lg={8}>
            <QuoteResults
              quote={quote}
              isLoading={isLoading}
              onAction={handleQuoteAction}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          py: 3,
          mt: 6,
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          display="block"
        >
          Mobility Safe © 2024 - Piattaforma preventivazione dealer
        </Typography>
      </Box>
    </>
  );
}
